import { getCityStateFromCoordinates } from 'services/location/location.service';
import {
  buildGlobalActionIpawsTemplate,
  getIpawsActionIntervalDuration,
  getIpawsActionIssues,
} from '../../../utils/ipawsActionPayload.utils';
import { createEmptyAction, createEmptyCondition } from '../../../utils/normalizeInitialData.utils';
import { builderComponentRegistry } from './builderComponentRegistry.utils';

export const getSectionCategoryOptions = (section) => {
  return Object.values(builderComponentRegistry)
    .filter((entry) => entry.sections.includes(section))
    .map((entry) => entry.category);
};

export const createBuilderItem = (section, category = '') => {
  const baseItem = section === 'actions' ? createEmptyAction() : createEmptyCondition();

  if (!category) {
    return baseItem;
  }

  return {
    ...baseItem,
    category,
    formData: builderComponentRegistry[category]?.createInitialFormData?.() ?? {},
  };
};

export const getRowLabel = (section, groupIndex, rowIndex) => {
  if (section === 'actions') {
    return rowIndex === 0 ? 'THEN' : 'AND';
  }

  if (groupIndex === 0 && rowIndex === 0) {
    return section === 'until' ? 'UNTIL' : 'IF';
  }

  if (rowIndex === 0) {
    return 'OR';
  }

  return 'AND';
};

const countItems = (items) => {
  return items.reduce((count, item) => count + (item.category ? 1 : 0), 0);
};

const countGroupedItems = (groups) => {
  return groups.reduce((count, group) => count + countItems(group), 0);
};

const getSingleCategoryName = (items) => {
  const populatedItems = items.filter((item) => item.category);
  return populatedItems.length === 1 ? populatedItems[0].category : 'Multiple';
};

const getSingleGroupedCategoryName = (groups) => {
  const populatedItems = groups.flat().filter((item) => item.category);
  return populatedItems.length === 1 ? populatedItems[0].category : 'Multiple';
};

const serializeCondition = (condition, originalState) => {
  const { category, formData } = condition;
  switch (category) {
    case 'Device':
      return {
        type: 'device',
        connection: formData.deviceType,
        deviceId: formData.selectedDevice?.id,
        nodeId: formData.selectedDevice?.node_id,
        value1: {
          type: 'key',
          value: formData.keyToCheck,
        },
        operation: formData.operator,
        value2: {
          type: formData.inputValueType ? 'immediate' : 'key',
          value: formData.inputValueType ? formData.valueToCompare : formData.keyToCompare,
        },
      };
    case 'Device Group':
      return {
        type: 'deviceGroup',
        connection: formData.deviceType,
        groupId: formData.group?.id,
        value1: {
          type: 'key',
          value: formData.keyToCheck,
        },
        operation: formData.operator,
        value2: {
          type: formData.inputValueType ? 'immediate' : 'key',
          value: formData.inputValueType ? formData.valueToCompare : formData.keyToCompare,
        },
      };
    case 'Date': {
      const [year, month, day] = (formData.date || '').split('-').map(Number);
      const date = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));

      return {
        type: 'date',
        value: {
          value: date,
        },
      };
    }
    case 'Time': {
      const [hours, minutes] = (formData.time || '').split(':').map(Number);
      const date = new Date();
      date.setHours(hours, minutes, 0, 0);

      return {
        type: 'time',
        value: {
          value: date,
        },
      };
    }
    case 'Date & Time':
      return {
        type: 'datetime',
        value: {
          value: new Date(formData.dateTime),
        },
      };
    case 'Original Condition Resolved':
      // For triggerresolve, include the first trigger in the same format as sent
      const triggers =
        originalState && originalState.triggers
          ? originalState.triggers
          : serializeGroupedConditions(originalState?.conditions || [], originalState);
      return {
        type: 'triggerresolve',
        value: {
          trigger: triggers,
        },
      };
    case 'NWS Alert':
      return {
        type: 'nwsalert',
        value: {
          value: formData.value,
        },
      };
    case 'Fusion Alert':
      return {
        type: 'fusionalert',
        value: {
          alertType: formData.selectedAlertType,
        },
      };
    default:
      return null;
  }
};

/**
 * Fetches city and state for all Create Radial Alert device actions.
 * Fires requests concurrently and patches results into each matching action's formData.
 * Non-blocking — if a fetch fails, the original action is returned unchanged.
 *
 * @param {Array<Object>} actions - The actions array from builder state.
 * @returns {Promise<Array<Object>>} A new actions array with `city` and `state` patched into
 *   formData for any Device Action of type 'Create Radial Alert' that has a valid device location.
 */
export const getCityStateforAlert = async (actions) => {
  const patchedActions = await Promise.all(
    actions.map(async (action) => {
      // Skip if not a Create Radial Alert device action or missing location
      if (
        action?.category === 'Device Action' &&
        action?.formData?.selectedFunction === 'Create Radial Alert' &&
        action?.formData?.selectedDevice?.location?.lat &&
        action?.formData?.selectedDevice?.location?.lng
      ) {
        try {
          const res = await getCityStateFromCoordinates(
            action.formData.selectedDevice.location.lat,
            action.formData.selectedDevice.location.lng,
          );
          if (res?.data?.result) {
            return {
              ...action,
              formData: {
                ...action.formData,
                city: res.data.result.city_name,
                state: res.data.result.state_name,
              },
            };
          }
        } catch (e) {
          // Non-blocking: proceed without city/state if fetch fails
        }
      }
      return action;
    }),
  );
  return patchedActions;
};

const serializeAction = (action, originalState) => {
  const { category, formData } = action;

  switch (category) {
    case 'Device Action':
      return {
        function: 'deviceAction',
        params: {
          ...formData,
          deviceId: formData.selectedDevice?.id,
          nodeId: formData.selectedDevice?.node_id,
          actionType: formData.selectedFunction,
        },
      };
    case 'Device Group Action':
      return {
        function: 'deviceGroupAction',
        params: {
          ...formData,
          actionType: formData.selectedFunction,
        },
      };
    case 'Send Email':
      return {
        function: 'sendEmail',
        params: {
          ...formData,
        },
      };
    case 'Send SMS':
      return {
        function: 'sendSMS',
        params: {
          ...formData,
        },
      };
    case 'Publish IPAWS Alert': {
      // The template build refuses the save when an issue remains, a missing or zero
      // interval included. The service stamps the effective and expiration times from the
      // interval (card BE-25), so the template has neither. The params carry no approval: the
      // backend derives `approvalStatus` from the message status (Draft = draft, else approved).
      // Multi-COG contract §1 (contractVersion 3): the class sits beside the template as
      // `environment`. The template build refuses the save while the action has no class.
      const template = buildGlobalActionIpawsTemplate(formData);
      return {
        function: 'ipaws',
        params: {
          contractVersion: 3,
          environment: formData.femaEnvironment,
          intervalDuration: getIpawsActionIntervalDuration(formData),
          ...(Array.isArray(formData.sent) ? { sent: formData.sent } : {}),
          template,
        },
      };
    }
    case 'NWS Alert':
      return {
        function: 'nwsAlert',
        params: {
          ...formData,
        },
      };
    default:
      return null;
  }
};

const serializeGroupedConditions = (groups, originalState) => {
  return groups
    .map((group) =>
      group.map((condition) => serializeCondition(condition, originalState)).filter(Boolean),
    )
    .filter((group) => group.length > 0);
};

const serializeActions = (actions, originalState) => {
  return actions.map((action) => serializeAction(action, originalState)).filter(Boolean);
};

// Formats the builder state into the payload structure expected by the backend when creating/updating a global action
export const buildGlobalActionPayload = async (builderState) => {
  const patchedActions = await getCityStateforAlert(builderState.actions);
  const triggers = serializeGroupedConditions(builderState.conditions, builderState);
  const actions = serializeActions(patchedActions, builderState);
  const resolutions = serializeGroupedConditions(builderState.until, builderState);
  const triggerCount = countGroupedItems(builderState.conditions);
  const actionCount = countItems(builderState.actions);
  const resolutionCount = countGroupedItems(builderState.until);

  return {
    state: 'inactive',
    title: builderState.metadata.title || 'New Global Action',
    debounceTime: Number(builderState.metadata.debounceTime || 0),
    minEventTime: Number(builderState.metadata.minEventTime || 0),
    actionName: actionCount === 1 ? getSingleCategoryName(builderState.actions) : 'Multiple',
    triggerName:
      triggerCount === 1 ? getSingleGroupedCategoryName(builderState.conditions) : 'Multiple',
    resolutionName:
      resolutionCount === 0
        ? 'none'
        : resolutionCount === 1
          ? getSingleGroupedCategoryName(builderState.until)
          : 'Multiple',
    triggers,
    actions,
    resolutions,
    rawDataList: JSON.stringify([
      {
        version: 2,
        conditions: builderState.conditions,
        actions: builderState.actions,
        until: builderState.until,
      },
    ]),
  };
};

const getComparableValue = (item) => {
  if (!item?.category) return null;

  switch (item.category) {
    case 'Date & Time':
      return item.formData?.dateTime ? new Date(item.formData.dateTime).getTime() : null;
    case 'Date': {
      if (!item.formData?.date) return null;
      const [year, month, day] = item.formData.date.split('-').map(Number);
      return Date.UTC(year, month - 1, day, 12, 0, 0);
    }
    case 'Time': {
      if (!item.formData?.time) return null;
      const [hours, minutes] = item.formData.time.split(':').map(Number);
      return hours * 60 + minutes;
    }
    default:
      return null;
  }
};

export const validateBuilderState = (builderState) => {
  const rowErrors = {};
  const formErrors = [];

  if (!builderState.metadata.title?.trim()) {
    formErrors.push('A TITLE is required.');
  }

  builderState.conditions.flat().forEach((condition) => {
    if (!condition.category) {
      rowErrors[condition.id] = 'Select an IF trigger.';
      return;
    }

    if (condition.formData?.error) {
      rowErrors[condition.id] = 'Check this row for missing fields.';
    }
  });

  builderState.actions.forEach((action) => {
    if (!action.category) {
      rowErrors[action.id] = 'Select a THEN action.';
      return;
    }

    if (action.category === 'Publish IPAWS Alert') {
      const issues = getIpawsActionIssues(action.formData);
      if (issues.length) rowErrors[action.id] = issues;
    } else if (action.formData?.error) {
      rowErrors[action.id] = 'Check this row for missing fields.';
    }
  });

  builderState.until.flat().forEach((condition) => {
    if (!condition.category) {
      rowErrors[condition.id] = 'Select an UNTIL resolution.';
      return;
    }

    if (condition.category !== 'Original Condition Resolved' && condition.formData?.error) {
      rowErrors[condition.id] = 'Check this row for missing fields.';
    }
  });

  const ifComparablesByCategory = new Map();
  builderState.conditions.flat().forEach((condition) => {
    const value = getComparableValue(condition);
    if (value === null) return;
    ifComparablesByCategory.set(condition.category, [
      ...(ifComparablesByCategory.get(condition.category) ?? []),
      value,
    ]);
  });

  builderState.until.flat().forEach((condition) => {
    const untilValue = getComparableValue(condition);
    const ifValues = ifComparablesByCategory.get(condition.category);

    if (untilValue === null || !ifValues?.length) {
      return;
    }

    if (ifValues.some((ifValue) => untilValue < ifValue)) {
      rowErrors[condition.id] = 'UNTIL date/time must not be earlier than IF.';
    }
  });

  //Error Building for the form as a whole, not specific to any row
  if (builderState.conditions.flat().length === 0) {
    formErrors.push('Add at least one IF trigger.');
  }

  const hasTriggerRowErrors = builderState.conditions
    .flat()
    .some((condition) => rowErrors[condition.id]);
  if (hasTriggerRowErrors) {
    formErrors.push('Complete the IF trigger.');
  }

  if (builderState.actions.length === 0) {
    formErrors.push('Add at least one THEN action.');
  }

  const hasActionRowErrors = builderState.actions.some((action) => rowErrors[action.id]);
  if (hasActionRowErrors) {
    formErrors.push('Complete the THEN action.');
  }

  if (builderState.until.length === 0 || builderState.until.flat().length === 0) {
    formErrors.push('Add at least one UNTIL resolution.');
  }

  const hasUntilRowErrors = builderState.until.flat().some((condition) => rowErrors[condition.id]);
  if (hasUntilRowErrors) {
    formErrors.push('Complete the UNTIL resolution.');
  }

  const hasUntilDateTimeError = builderState.until
    .flat()
    .some(
      (condition) => rowErrors[condition.id] === 'UNTIL date/time must not be earlier than IF.',
    );
  if (hasUntilDateTimeError) {
    formErrors.push('UNTIL date/time must not be earlier than IF.');
  }

  const formError = formErrors[0] || '';

  return {
    isValid: formErrors.length === 0 && Object.keys(rowErrors).length === 0,
    rowErrors,
    formError,
    formErrors,
  };
};
